import { toast } from 'sonner';
import type { ContextCompactionState } from '../types/session.types';
import type { MemoryUsage } from '../types/websocket.types';
import { formatMemoryPercent, memoryPercentOf } from '../utils/memoryUsageFormat';

/** Repli quand le backend ne fournit pas de `userMessage` (le contrat le promet sur error/refused). */
export const CONTEXT_COMPACTION_FAILURE_FALLBACK =
  "La compaction n'a pas pu aboutir. L'historique est conservé intact.";

/** Seuils de la dernière jauge connue de la session, pour exprimer l'avant / après en pourcentage. */
export type ContextCompactionGauge = Pick<MemoryUsage, 'compactionThreshold' | 'tokensMax'>;

/**
 * Retour visible d'une compaction manuelle terminée.
 *
 * WHY dans le pont du provider et pas dans le composer : le verdict peut arriver
 * après que l'avocat a changé de conversation ou refermé le dock. Monté une fois
 * dans la coque, le pont voit chaque verdict exactement une fois, là où un
 * composer ne le verrait que s'il est encore à l'écran — et deux composers de la
 * même session l'afficheraient deux fois.
 *
 * Le succès se lit en pourcentages de mémoire, comme le rond du composer, jamais
 * en « tokens ». Sans jauge (conversation rouverte : aucune mesure n'est encore
 * arrivée sur cette socket) ou sans chiffres du backend, le toast reste sans
 * description plutôt que d'inventer un seuil.
 *
 * `idle` et `running` ne produisent rien : le rond du composer porte déjà l'état
 * en cours.
 */
export function notifyContextCompactionOutcome(
  state: ContextCompactionState,
  gauge: ContextCompactionGauge | null,
): void {
  switch (state.status) {
    case 'completed': {
      const description = describeMemoryReduction(state, gauge);
      toast.success('Conversation compactée', description ? { description } : undefined);
      return;
    }
    case 'error':
    case 'refused':
      toast.error(state.userMessage?.trim() || CONTEXT_COMPACTION_FAILURE_FALLBACK);
      return;
    case 'idle':
    case 'running':
      return;
  }
}

/** « Mémoire : 72,4 % → 18 % », ou `null` s'il manque un chiffre. */
function describeMemoryReduction(
  { tokensBefore, tokensAfter }: ContextCompactionState,
  gauge: ContextCompactionGauge | null,
): string | null {
  if (!gauge || tokensBefore === undefined || tokensAfter === undefined) return null;
  const before = memoryPercentOf(tokensBefore, gauge);
  const after = memoryPercentOf(tokensAfter, gauge);
  if (before === null || after === null) return null;
  return `Mémoire\u00a0: ${formatMemoryPercent(before)} → ${formatMemoryPercent(after)}`;
}
